// frontend/src/pages/MacroRegimePanel.js
import React, { useEffect, useState } from 'react';
import '../styles/MacroRegimePanel.css'; // we'll add this file below
import axios from "axios";


function rotationStateLabel(state) {
  switch (state) {
    case 'capital-defensive':
      return 'Rotation: Capital Defensive (BTC & Stablecoins Favoured)';
    case 'rotation-on':
      return 'Rotation: On (Capital Leaving BTC/USDT into Alts)';
    case 'btc-only':
      return 'Rotation: BTC-Only (BTC Favoured, Alts Lag)';
    default:
      return 'Rotation: Neutral / Unclear';
  }
}

function rotationBadgeClass(state) {
  switch (state) {
    case 'capital-defensive':
      return 'macro-badge macro-badge-danger';
    case 'rotation-on':
      return 'macro-badge macro-badge-success';
    case 'btc-only':
      return 'macro-badge macro-badge-info';
    default:
      return 'macro-badge macro-badge-neutral';
  }
}


function badgeClass(regime) {
  switch (regime) {
    case 'risk-on':
      return 'macro-badge macro-badge-success';
    case 'early-risk-on':
      return 'macro-badge macro-badge-warning';
    case 'bottoming-risk-off':
      return 'macro-badge macro-badge-info';
    case 'risk-off':
      return 'macro-badge macro-badge-danger';
    default:
      return 'macro-badge macro-badge-neutral';
  }
}

function fmtPct(v) {
  if (v == null) return '-';
  return `${v.toFixed(2)}%`;
}

function macroRegimeLabel(regime) {
  switch (regime) {
    case 'risk-on':
      return 'Risk-On (Growth / Appetite Increasing)';
    case 'early-risk-on':
      return 'Early Risk-On (Tentative / Improving)';
    case 'bottoming-risk-off':
      return 'Late Risk-Off (Panic / Bottoming Zone)';
    case 'risk-off':
      return 'Risk-Off (Defensive / Fear Dominant)';
    default:
      return 'Neutral / Mixed Macro';
  }
}

function macroRegimeTooltip(regime) {
  switch (regime) {
    case 'risk-on':
      return 'Risk appetite strengthening, growth sectors leading, fear indicators calm.';
    case 'early-risk-on':
      return 'Risk appetite improving after fear spikes; confirmation still needed.';
    case 'bottoming-risk-off':
      return 'Fear is elevated (risk-off), but this type of panic often appears late in sell-offs and can precede bottoms.';
    case 'risk-off':
      return 'Investors are defensive; cash and safe assets preferred.';
    default:
      return 'Macro signals are mixed without a strong directional edge.';
  }
}

function cryptoRegimeLabel(regime) {
  switch (regime) {
    case 'alts-season':
      return 'Alts Season (Alt Dominance / Expansion)';
    case 'alts-preseason':
      return 'Alts Preseason (Early Rotation Forming)';
    case 'btc-only':
      return 'BTC-Led Risk-On';
    case 'risk-off':
      return 'Crypto Risk-Off (BTC & USDT Dominate)';
    case 'range':
    default:
      return 'Range / Chop';
  }
}

function combinedDirectionLabel(dir) {
  switch (dir) {
    case 'Bullish':
      return 'Short-Term Bias: Bullish';
    case 'Cautious Bullish':
      return 'Short-Term Bias: Cautious Bullish';
    case 'Cautious Bearish':
      return 'Short-Term Bias: Cautious Bearish';
    case 'Bearish':
      return 'Short-Term Bias: Bearish';
    default:
      return 'Short-Term Bias: Neutral';
  }
}

function cryptoBadgeClass(cryptoRegime) {
  switch (cryptoRegime) {
    case 'alts-season':
      return 'macro-badge macro-badge-success';
    case 'alts-preseason':
      return 'macro-badge macro-badge-warning';
    case 'btc-only':
      return 'macro-badge macro-badge-info';
    case 'risk-off':
      return 'macro-badge macro-badge-danger';
    case 'range':
    default:
      return 'macro-badge macro-badge-neutral';
  }
}


export default function MacroRegimePanel() {
  const userId = localStorage.getItem('userId');
  const isAdmin = userId === 'pziPDtOgJiX06oGuJxNwxE';

  const [data, setData] = useState(null);          // current view (latest or live)
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);  // selected history row
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isLocal = window.location.hostname === "localhost";
  const API_BASE = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";


  const loadLatest = async () => {
  try {
    setLoading(true);
    setError('');
    const res = await axios.get(`${API_BASE}/api/macro/regime/latest`);

    if (!res.data.ok) {
      throw new Error(res.data.error || 'Failed to load latest macro regime');
    }

    if (res.data.empty) {
      // ✅ No snapshots yet – just clear current data and don’t show an error
      setData(null);
      setSelected(null);
      return;
    }

    setData(res.data);
    setSelected(null);
  } catch (e) {
    setError(e.message || 'Failed to load latest macro regime');
  } finally {
    setLoading(false);
  }
};


  const loadHistory = async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API_BASE}/api/macro/regime/history`, { params: { limit: 50 } });
      if (!res.data.ok) throw new Error(res.data.error || 'Failed to load history');
      setHistory(res.data.items || []);
    } catch (e) {
      console.error('History load error:', e.message);
    }
  };

  // initial load
  useEffect(() => {
    loadLatest();
    if (isAdmin) {
      loadHistory();
    }
  }, [isAdmin]);

  const handleRunLive = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_BASE}/api/macro/regime/live`);
      if (!res.data.ok) throw new Error(res.data.error || 'Live macro call failed');
      setData(res.data);
    } catch (e) {
      setError(e.message || 'Live macro call failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToDb = async () => {
    try {
      setSaving(true);
      setError('');
      const res = await axios.post(`${API_BASE}/api/macro/regime/save`);
      if (!res.data.ok) throw new Error(res.data.error || 'Macro save failed');
      setData(res.data);
      await loadHistory();
    } catch (e) {
      setError(e.message || 'Macro save failed');
    } finally {
      setSaving(false);
    }
  };

  const current = data;
  const metrics = current?.metrics || {};
  const idx = metrics.indices || {};
  const cg = metrics.cryptoGlobal || {};
  const funding = metrics.btcFunding || {};
  const cryptoSummary = current?.cryptoRegimeSummary || null;

  const renderTableRow = (key) => {
    const row = idx[key];
    if (!row) return null;
    const chg = row.changePercent;
    return (
      <tr key={key}>
        <td>{row.symbol}</td>
        <td>{row.price?.toFixed?.(2)}</td>
        <td className={chg > 0 ? 'macro-cell-red' : chg < 0 ? 'macro-cell-green' : ''}>
          {fmtPct(chg)}
        </td>
      </tr>
    );
  };

  const effectiveTimestamp = current?.timestamp
    ? new Date(current.timestamp)
    : null;

  return (
    <div className="macro-container">
      <div className="macro-header-row">
        <div>
          <h2 className="macro-title">
            MACRO REGIME{' '}
            {current && (
              <span
                className={badgeClass(current.regime)}
                title={macroRegimeTooltip(current.regime)}
              >
                {macroRegimeLabel(current.regime)}
              </span>
            )}
          </h2>
            {effectiveTimestamp && (
            <div className="macro-subtitle">
                Last updated: {effectiveTimestamp.toLocaleString()}
                {current?.source && ` • Source: TrendScout`}
            </div>
            )}
                {cryptoSummary && (
                  <div className="macro-subtitle">
                    Crypto regime:{' '}
                    <span
                      className={cryptoBadgeClass(cryptoSummary.cryptoRegime)}
                      title={cryptoRegimeLabel(cryptoSummary.cryptoRegime)}
                    >
                      {cryptoRegimeLabel(cryptoSummary.cryptoRegime)}
                    </span>
                    {' • '}
                    <span className="macro-chip">
                      {combinedDirectionLabel(cryptoSummary.combinedDirection24h)}
                    </span>
                    {cryptoSummary.rotationState && (
                      <>
                        {' • '}
                        <span
                          className={rotationBadgeClass(cryptoSummary.rotationState)}
                          title={rotationStateLabel(cryptoSummary.rotationState)}
                        >
                          {rotationStateLabel(cryptoSummary.rotationState)}
                        </span>
                      </>
                    )}
                  </div>
                )}

        </div>

        {isAdmin && (
          <div className="macro-actions">
            <button
              className="macro-btn macro-btn-outline"
              onClick={handleRunLive}
              disabled={loading || saving}
            >
              {loading && current?.source === 'live' ? 'Running…' : 'Run Live'}
            </button>
            <button
              className="macro-btn macro-btn-primary"
              onClick={handleSaveToDb}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save to DB'}
            </button>
            <button
              className="macro-btn macro-btn-soft"
              onClick={loadLatest}
              disabled={loading || saving}
            >
              Load Latest Saved
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="macro-alert macro-alert-error">
          {error}
        </div>
      )}

      {loading && !current && (
        <div className="macro-loading">Loading macro regime…</div>
      )}

      {current && (
        <>
          {/* SUMMARY */}
            <section className="macro-section">
            <h3 className="macro-section-title">Macro Summary</h3>

            {current.buckets ? (
                <div className="macro-summary-grid">
                <div className="macro-summary-column">
                    <h4 className="macro-summary-heading macro-summary-bearish">Bearish</h4>
                    <ul className="macro-notes">
                    {current.buckets.bearish?.length
                        ? current.buckets.bearish.map((n, i) => <li key={i}>{n}</li>)
                        : <li className="macro-note-empty">No strong bearish signals</li>}
                    </ul>
                </div>

                <div className="macro-summary-column">
                    <h4 className="macro-summary-heading macro-summary-conflict">Conflicting / Neutral</h4>
                    <ul className="macro-notes">
                    {current.buckets.conflict?.length
                        ? current.buckets.conflict.map((n, i) => <li key={i}>{n}</li>)
                        : <li className="macro-note-empty">No major conflict</li>}
                    </ul>
                </div>

                <div className="macro-summary-column">
                    <h4 className="macro-summary-heading macro-summary-bullish">Bullish</h4>
                    <ul className="macro-notes">
                    {current.buckets.bullish?.length
                        ? current.buckets.bullish.map((n, i) => <li key={i}>{n}</li>)
                        : <li className="macro-note-empty">No strong bullish signals</li>}
                    </ul>
                </div>
                </div>
            ) : (
                // fallback for older responses without buckets
                <ul className="macro-notes">
                {current.notes?.map((n, i) => (
                    <li key={i}>{n}</li>
                ))}
                </ul>
            )}
            </section>

                {/* CRYPTO SUMMARY */}
                {cryptoSummary && (
                <section className="macro-section">
                  <h3 className="macro-section-title">
                    Crypto Summary
                    <span
                      className={cryptoBadgeClass(cryptoSummary.cryptoRegime)}
                      style={{ marginLeft: 8 }}
                      title={cryptoRegimeLabel(cryptoSummary.cryptoRegime)}
                    >
                      {cryptoRegimeLabel(cryptoSummary.cryptoRegime)}
                    </span>
                    <span className="macro-chip">
                      {combinedDirectionLabel(cryptoSummary.combinedDirection24h)}
                    </span>
                  </h3>
                  {cryptoSummary.liquidityCaution && (
                      <div className="macro-alert macro-alert-warning" style={{ marginTop: 8 }}>
                        Liquidity caution: top crypto volume is trending down with flat funding. 
                        Breakout trades are less reliable – pullback-based entries are safer.
                      </div>
                    )}
                    <div className="macro-summary-grid">
                    <div className="macro-summary-column">
                        <h4 className="macro-summary-heading macro-summary-bearish">Bearish</h4>
                        <ul className="macro-notes">
                        {cryptoSummary.buckets?.bearish?.length
                            ? cryptoSummary.buckets.bearish.map((n, i) => <li key={i}>{n}</li>)
                            : <li className="macro-note-empty">No strong bearish signals</li>}
                        </ul>
                    </div>

                    <div className="macro-summary-column">
                        <h4 className="macro-summary-heading macro-summary-conflict">Conflicting / Neutral</h4>
                        <ul className="macro-notes">
                        {cryptoSummary.buckets?.conflict?.length
                            ? cryptoSummary.buckets.conflict.map((n, i) => <li key={i}>{n}</li>)
                            : <li className="macro-note-empty">No major conflict</li>}
                        </ul>
                    </div>

                    <div className="macro-summary-column">
                        <h4 className="macro-summary-heading macro-summary-bullish">Bullish</h4>
                        <ul className="macro-notes">
                        {cryptoSummary.buckets?.bullish?.length
                            ? cryptoSummary.buckets.bullish.map((n, i) => <li key={i}>{n}</li>)
                            : <li className="macro-note-empty">No strong bullish signals</li>}
                        </ul>
                    </div>
                    </div>
                </section>
                )}


          {/* MAIN GRID */}
          <section className="macro-grid">
            <div className="macro-card">
              <h4 className="macro-card-title">Key Indices</h4>
              <table className="macro-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Price</th>
                    <th>Δ%</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    'vix',
                    'vix9d',
                    'spx',
                    'ndx',
                    'dji',
                    'dxy',
                    'iwm',
                    'soxx',
                    'wti',
                    'tlt',
                    'tnx',
                  ].map(renderTableRow)}
                </tbody>
              </table>
            </div>

            <div className="macro-card">
              <h4 className="macro-card-title">Crypto Overview</h4>
              <table className="macro-table">
                <tbody>
                  <tr>
                    <td>Total Mcap</td>
                    <td>
                      {cg.totalMcap
                        ? cg.totalMcap.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })
                        : '-'}
                    </td>
                  </tr>
                  <tr>
                    <td>BTC Dominance</td>
                    <td>{cg.btcDominance ? fmtPct(cg.btcDominance) : '-'}</td>
                  </tr>
                  <tr>
                    <td>USDT Dominance</td>
                    <td>{cg.usdtDominance ? fmtPct(cg.usdtDominance) : '-'}</td>
                  </tr>
                  <tr>
                    <td>BTC Funding (8h)</td>
                    <td>
                      {funding.lastFundingRate != null
                        ? funding.lastFundingRate.toExponential(2)
                        : '-'}
                    </td>
                  </tr>
                  <tr>
                    <td>BTC Mark Price</td>
                    <td>
                      {funding.markPrice
                        ? funding.markPrice.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })
                        : '-'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {/* HISTORY (ADMIN ONLY) */}
      {isAdmin && (
        <section className="macro-section macro-history-section">
          <div className="macro-history-header">
            <h3 className="macro-section-title">Saved Macro History</h3>
            <span className="macro-history-sub">
              Showing last {history.length} records
            </span>
          </div>

          <div className="macro-history-grid">
            <div className="macro-card macro-history-table-card">
              <table className="macro-table macro-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Regime</th>
                    <th>VIX</th>
                    <th>SPX %</th>
                    <th>IWM %</th>
                    <th>SOXX %</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((doc) => {
                    const id = doc._id;
                    const asOf = doc.asOf ? new Date(doc.asOf) : null;
                    const vix = doc.indices?.vix?.price;
                    const spxChg = doc.indices?.spx?.changePercent;
                    const iwmChg = doc.indices?.iwm?.changePercent;
                    const soxxChg = doc.indices?.soxx?.changePercent;
                    const isSelected = selected && selected._id === id;

                    return (
                      <tr
                        key={id}
                        className={isSelected ? 'macro-row-selected' : 'macro-row-clickable'}
                        onClick={() => setSelected(doc)}
                      >
                        <td>{asOf ? asOf.toLocaleString() : '-'}</td>
                        <td>{doc.regime}</td>
                        <td>{vix != null ? vix.toFixed(2) : '-'}</td>
                        <td>{spxChg != null ? fmtPct(spxChg) : '-'}</td>
                        <td>{iwmChg != null ? fmtPct(iwmChg) : '-'}</td>
                        <td>{soxxChg != null ? fmtPct(soxxChg) : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

                    {selected && (
                    <div className="macro-card macro-history-detail-card">
                        <h4 className="macro-card-title">
                        Snapshot Details
                        <span
                          className={badgeClass(selected.regime)}
                          style={{ marginLeft: 8 }}
                          title={macroRegimeTooltip(selected.regime)}
                        >
                          {macroRegimeLabel(selected.regime)}
                        </span>
                        {selected.cryptoRegimeSummary && (
                          <span
                            className={cryptoBadgeClass(selected.cryptoRegimeSummary.cryptoRegime)}
                            style={{ marginLeft: 8 }}
                            title={cryptoRegimeLabel(selected.cryptoRegimeSummary.cryptoRegime)}
                          >
                            {cryptoRegimeLabel(selected.cryptoRegimeSummary.cryptoRegime)}
                          </span>
                        )}
                        </h4>
                        <div className="macro-history-detail-meta">
                        {selected.asOf && `As of: ${new Date(selected.asOf).toLocaleString()}`}
                        </div>

                        {selected.cryptoRegimeSummary && (
                        <div className="macro-history-detail-meta">
                            {combinedDirectionLabel(selected.cryptoRegimeSummary.combinedDirection24h)}
                        </div>
                        )}

                        <ul className="macro-notes">
                        {selected.notes?.map((n, i) => (
                            <li key={i}>{n}</li>
                        ))}
                        </ul>
                    </div>
                    )}

          </div>
        </section>
      )}
    </div>
  );
}
